import { Table, message, TableProps, Row, Col, Grid } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import config from 'config';
import {
  Order,
  PermissionsEnum,
  ProductListFilterInput,
  ProductListSort,
  ProductOutput,
  useGetProductsLazyQuery,
  useMakeProductAvailableMutation,
  useGetRegionsQuery,
} from 'gql/types';
import { Spin } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import { useFilter, useModal, useSort } from 'modules/common/hooks';
import { useRegionFilterInit } from 'modules/common/hooks/useRegionFilterInit';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import LabelsInfoModal from 'modules/products/components/LabelsInfoModal';
import {
  createProductsTableChipsConfig,
  excludedFilterFields,
} from 'modules/products/configs/productsTableFilters';
import { useWoocommerceCategories } from 'modules/products/hooks/useWoocommerceCategories';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { useUserPermissionsAndRegion } from 'modules/user/hooks/useUserPermissionsAndRegion';
import { FC, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { envServices } from 'utils/EnvServices';

import { getColumns } from './columns';
import Header from './Header';

const { CreateLabels, MakeProductsAvailable } = PermissionsEnum;
const { useBreakpoint } = Grid;

const ProductsTable: FC = () => {
  const { t } = useTranslation('products.ProductsTable');

  const isAllowedProductCreation = envServices.get('PRODUCT_CREATION_ALLOWED');
  const currentUser = useCurrentUser();
  const canCreateLabels =
    currentUser.isCan(CreateLabels) && isAllowedProductCreation;
  const canMakeProductsAvailable =
    currentUser.isCan(MakeProductsAvailable) && isAllowedProductCreation;

  const [selectedProducts, setSelectedProducts] = useState<ProductOutput[]>([]);
  const productListSort = useSort<ProductListSort>();
  const filter = useFilter<ProductListFilterInput>();
  const pagination = usePagination();

  const { categories, loading: categoriesLoading } = useWoocommerceCategories();

  const { assignedRegionNames, canViewAllRegions, hasRegionRestrictions } =
    useUserPermissionsAndRegion();

  const { isLoading: isRegionalFilterLoading } =
    useRegionFilterInit<ProductListFilterInput>({
      filterHook: filter,
      filterFieldName: 'regionName',
      canViewAllRegions,
      hasRegionRestrictions,
      assignedRegionNames,
      successMessage: t('regionalFilterApplied'),
    });

  const { data: allAvailableRegionsData } = useGetRegionsQuery({});

  const regionFilterOptions = useMemo(
    () =>
      allAvailableRegionsData?.getRegions?.map((region) => ({
        text: region.name,
        value: region.name,
      })),
    [allAvailableRegionsData]
  );

  const chipsConfig = useMemo(() => createProductsTableChipsConfig(t), [t]);

  const productFilters = useMemo(
    () => filter.getFilterObject(),
    [filter.options]
  );

  const labelsInfoModal = useModal<{ products?: ProductOutput[] }>();

  const [makeProductAvailable, makeProductAvailableMutation] =
    useMakeProductAvailableMutation({
      refetchQueries: ['getProducts'],
      onCompleted: () => {
        message.success(t('productAvailable'));
      },
      onError: (error) => {
        message.error(error.message);
      },
    });

  const productCategories = useMemo(() => {
    if (!categories) {
      return undefined;
    }

    return categories.reduce((acc, category) => {
      return { ...acc, [category.id]: category.name };
    }, {} as Record<number, string>);
  }, [categories]);

  const selectedProductsIds = selectedProducts.map(({ id }) => id);

  const [getProducts, productsQuery] = useGetProductsLazyQuery();

  useEffect(() => {
    if (isRegionalFilterLoading) {
      return;
    }

    getProducts({
      variables: {
        input: {
          skip: pagination.skip,
          take: config.PAGINATION.DEFAULT_LIMIT,
          filter: productFilters,
          sort: productListSort.options,
        },
      },
      onCompleted: ({ getProducts }) => {
        pagination.setTotal(getProducts.meta.total);
      },
      onError: (error) => {
        message.error(error.message);
      },
    });
  }, [
    productFilters,
    productListSort.options,
    pagination.skip,
    isRegionalFilterLoading,
  ]);

  const isLoading =
    isRegionalFilterLoading ||
    categoriesLoading ||
    productsQuery.loading ||
    makeProductAvailableMutation.loading;

  const products = productsQuery.data?.getProducts.data as
    | ProductOutput[]
    | undefined;

  const { md } = useBreakpoint();

  const productsTableColumns = useMemo(() => {
    return getColumns({
      productCategories,
      showSorterTooltip: md,
      canCreateLabels,
      canMakeProductsAvailable,
      filterValue: productFilters,
      sort: productListSort?.options[0],
      regionFilterOptions,
      onPrint: (product) => labelsInfoModal.show({ products: [product] }),
      onAvailable: ({ id }) => {
        makeProductAvailable({
          variables: {
            input: {
              id,
            },
          },
          refetchQueries: ['getProducts'],
        });
      },
    });
  }, [
    productCategories,
    md,
    canCreateLabels,
    canMakeProductsAvailable,
    productFilters,
    productListSort?.options[0],
    regionFilterOptions,
  ]);

  const onSearch = (searchQuery: string | undefined) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchQuery',
      value: searchQuery,
    });
  };

  const onTableChange: TableProps<ProductOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<ProductOutput>;
      const fieldName = tableSorter.columnKey as ProductListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        productListSort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        productListSort.removeSortOption(fieldName);
      }
    }

    if (extra.action === 'filter') {
      Object.entries(filters).forEach(([fieldName, value]) => {
        filter.setFilterOption({
          fieldName: fieldName as keyof ProductListFilterInput,
          value,
        });
      });

      filter.setFilterOption({
        fieldName: 'searchQuery',
        value: null,
      });
    }
  };

  const onSelect = (changedRow: ProductOutput, selected: boolean): void => {
    setSelectedProducts((previousProducts: ProductOutput[]) =>
      selected
        ? [...previousProducts, changedRow]
        : previousProducts.filter((p) => p.id !== changedRow.id)
    );
  };

  const onSelectAll = (
    selected: boolean,
    _selectedRows: ProductOutput[],
    changedRows: ProductOutput[]
  ) => {
    setSelectedProducts((previousProducts: ProductOutput[]) => {
      if (selected) {
        return [...previousProducts, ...changedRows];
      }

      const changedRowsIds = changedRows.map((row) => row.id);
      return previousProducts.filter((p) => !changedRowsIds.includes(p.id));
    });
  };

  return (
    <>
      <Spin spinning={isLoading}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Header onSearch={onSearch} selectedProducts={selectedProducts} />
          </Col>

          <Col span={24}>
            <FilterChips<ProductListFilterInput>
              filterHook={filter}
              config={chipsConfig}
              excludeFields={excludedFilterFields}
            />
          </Col>

          <Col span={24}>
            <Table
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedProductsIds,
                onSelect,
                onSelectAll,
              }}
              rowKey={({ id }) => id}
              columns={productsTableColumns}
              dataSource={products || []}
              onChange={onTableChange}
              pagination={pagination.config}
              scroll={{ x: 750 }}
            />
          </Col>
        </Row>
      </Spin>
      <LabelsInfoModal products={[]} {...labelsInfoModal.config} />
    </>
  );
};

export default ProductsTable;
